import { Button, Card, Classes, Dialog, Elevation, Icon, Spinner } from '@blueprintjs/core'
import { toast } from 'botpress/shared'
import React, { FC, useEffect, useState } from 'react'
import { useDispatch } from 'react-redux'
import { formDataStore } from '../../../token-store'
import BotDetailsDialog from './BotDetailsDialog'
import { updateBot } from './reducer'
import '../../user/modern-settings.css'

interface BotDetails {
  website?: string
  phoneNumber?: string
  termsConditions?: string
  privacyPolicy?: string
  emailAddress?: string
  avatarUrl?: string
  coverPictureUrl?: string
}

interface BotInfo {
  locked: boolean
  disabled: boolean
  private: boolean
  details: BotDetails
  description?: string
  active: boolean
  version: string
  author?: string
  license?: string
  imports: {
    modules?: string[]
    incomingMiddleware?: string[]
    outgoingMiddleware?: string[]
    contentTypes?: string[]
  }
  dialog?: {
    timeoutInterval: string
  }
  logs?: {
    expiration: string
  }
  defaultLanguage: string
  languages: string[]
  id: string
  name: string
  owner?: string
  pipeline_status?: {
    current_stage: {
      id: string
      promoted_on: string
      promoted_by: string
    }
  }
  messaging?: {
    channels?: {
      [channelName: string]: any
    }
  }
  // Legacy channel tokens (for backward compatibility)
  telegramToken?: string
  slackBotToken?: string
  messengerAccessToken?: string
  twilioAccountSid?: string
}

interface Props {
  isOpen: boolean
  toggle: () => void
}

const BotsDashboard: FC<Props> = ({ isOpen, toggle }) => {
  const [bots, setBots] = useState<BotInfo[]>([])
  const [isLoading, setIsLoading] = useState(true)
  const [selectedBot, setSelectedBot] = useState<BotInfo | null>(null)
  const [confirmDialogOpen, setConfirmDialogOpen] = useState(false)
  const [botToToggle, setBotToToggle] = useState<BotInfo | null>(null)
  const dispatch = useDispatch()

  useEffect(() => {
    if (isOpen) {
      loadBots()
    }
  }, [isOpen])

  const loadBots = () => {
    try {
      setIsLoading(true)
      const formData = formDataStore.get()

      if (!formData) {
        toast.warning('No bot data found in storage.')
        setBots([])
        setIsLoading(false)
        return
      }

      const parsedData = formData
      const filteredBots = parsedData.filteredBots

      if (!filteredBots || !Array.isArray(filteredBots)) {
        toast.warning('No bots found in the data.')
        setBots([])
        setIsLoading(false)
        return
      }

      setBots(filteredBots)
    } catch (error: any) {
      toast.failure(`Failed to load bots: ${error.message}`)
      setBots([])
    } finally {
      setIsLoading(false)
    }
  }

  const getActiveChannels = (bot: BotInfo): string[] => {
    const channels: string[] = []

    // Always include webchat as it's available by default
    channels.push('webchat')

    // Check messaging config (new format)
    if (bot.messaging?.channels) {
      Object.keys(bot.messaging.channels).forEach(channelName => {
        const channelConfig = bot.messaging!.channels![channelName]
        // Only include if channel is enabled
        if (channelConfig && channelConfig.enabled !== false) {
          channels.push(channelName)
        }
      })
    }

    // Check for legacy channel tokens (backward compatibility)
    if (bot.telegramToken) {
      channels.push('telegram')
    }
    if (bot.slackBotToken) {
      channels.push('slack')
    }
    if (bot.messengerAccessToken) {
      channels.push('messenger')
    }
    if (bot.twilioAccountSid) {
      channels.push('twilio')
    }

    return [...new Set(channels)] // Remove duplicates
  }

  const getChannelIcon = (channel: string): string => {
    const iconMap: { [key: string]: string } = {
      telegram: 'send-message',
      slack: 'chat',
      messenger: 'comment',
      twilio: 'phone',
      teams: 'people',
      smooch: 'widget',
      vonage: 'mobile-phone',
      webchat: 'globe',
      web: 'globe'
    }
    return iconMap[channel.toLowerCase()] || 'application'
  }

  const getChannelColor = (channel: string): string => {
    const colorMap: { [key: string]: string } = {
      telegram: '#0088cc',
      slack: '#4A154B',
      messenger: '#0084ff',
      twilio: '#F22F46',
      teams: '#6264A7',
      smooch: '#FF6900',
      vonage: '#000000',
      webchat: '#5C7CFA',
      web: '#5C7CFA'
    }
    return colorMap[channel.toLowerCase()] || '#5C7CFA'
  }

  const getBotAvatar = (bot: BotInfo): string => {
    if (bot.details?.avatarUrl) {
      return bot.details.avatarUrl
    }
    // Return a placeholder with first letter of bot name
    return `https://ui-avatars.com/api/?name=${encodeURIComponent(bot.name)}&background=5C7CFA&color=fff&size=128`
  }

  const handleBotClick = (bot: BotInfo) => {
    setSelectedBot(bot)
  }

  const handleCloseDetails = () => {
    setSelectedBot(null)
  }

  const getStatusColor = (bot: BotInfo): string => {
    if (bot.disabled) {
      return '#DC143C'
    }
    if (!bot.active) {
      return '#FFA500'
    }
    return '#28A745'
  }

  const getStatusText = (bot: BotInfo): string => {
    if (bot.disabled) {
      return 'Disabled'
    }
    if (!bot.active) {
      return 'Inactive'
    }
    return 'Active'
  }

  const toggleBotStatus = (bot: BotInfo, event: React.MouseEvent) => {
    event.stopPropagation() // Prevent card click event
    setBotToToggle(bot)
    setConfirmDialogOpen(true)
  }

  const handleConfirmToggle = async () => {
    if (!botToToggle) {
      return
    }

    const newDisabledState = !botToToggle.disabled
    const action = newDisabledState ? 'disabled' : 'enabled'

    try {
      // Update the bot via API
      dispatch(updateBot(botToToggle.id, { disabled: newDisabledState }))

      // Update selected bot if it's the one being toggled
      if (selectedBot?.id === botToToggle.id) {
        setSelectedBot({ ...botToToggle, disabled: newDisabledState })
      }

      toast.success(`Bot ${action} successfully`)

      // Close confirmation dialog
      setConfirmDialogOpen(false)
      setBotToToggle(null)

      // Reload the bots list
      loadBots()

      // Optionally close the main dashboard after a short delay
      setTimeout(() => {
        toggle()
      }, 1500)
    } catch (error: any) {
      console.error('Failed to toggle bot status:', error)
      toast.failure(`Failed to toggle bot status: ${error.message || 'Unknown error'}`)

      // Close confirmation dialog
      setConfirmDialogOpen(false)
      setBotToToggle(null)

      // Reload bots to ensure UI is in sync with backend
      loadBots()
    }
  }

  const handleCancelToggle = () => {
    setConfirmDialogOpen(false)
    setBotToToggle(null)
  }

  return (
    <>
      <Dialog
        title={`Bots Dashboard ${bots.length > 0 ? `(${bots.length} ${bots.length === 1 ? 'Bot' : 'Bots'})` : ''}`}
        icon="dashboard"
        isOpen={isOpen}
        onClose={toggle}
        transitionDuration={0}
        canOutsideClickClose={!isLoading}
        canEscapeKeyClose={!isLoading}
        className="modern-dialog"
        style={{ width: '95vw', maxWidth: '1600px', height: '94vh', maxHeight: '94vh' }}
      >
        <div
          className={Classes.DIALOG_BODY}
          style={{
            padding: '24px 28px 16px',
            height: 'calc(100% - 88px)',
            display: 'flex',
            flexDirection: 'column',
            overflow: 'hidden'
          }}
        >
          {/* Header & summary */}
          <div
            style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              marginBottom: '20px'
            }}
          >
            <div>
              <h3 style={{ margin: 0, fontSize: '20px', fontWeight: 600, color: '#0f172a' }}>Bots Overview</h3>
              <p style={{ margin: '4px 0 0', fontSize: '13px', color: '#64748b' }}>
                Manage the status, channels and details for all your bots.
              </p>
            </div>

            <div style={{ fontSize: '12px', color: '#64748b', display: 'flex', gap: '16px', fontWeight: 500 }}>
              <span>
                <strong>Total:</strong> {bots.length}
              </span>
              <span style={{ color: '#059669' }}>
                <strong>Active:</strong> {bots.filter(b => b.active && !b.disabled).length}
              </span>
              <span style={{ color: '#dc2626' }}>
                <strong>Disabled:</strong> {bots.filter(b => b.disabled).length}
              </span>
            </div>
          </div>

          <div className="modern-scrollbar" style={{ flex: 1, overflowY: 'auto', paddingRight: '8px' }}>
            {isLoading ? (
              <div
                style={{
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  justifyContent: 'center',
                  height: '400px'
                }}
              >
                <Spinner size={50} />
                <p style={{ marginTop: '20px', fontSize: '15px', fontWeight: 500, color: '#64748b' }}>Loading bots...</p>
              </div>
            ) : bots.length === 0 ? (
              <div style={{ textAlign: 'center', padding: '8px 0 60px 0' }}>
                <Icon icon="inbox" iconSize={60} style={{ color: '#cbd5e1', marginBottom: '20px' }} />
                <h3 style={{ color: '#64748b', marginBottom: '10px' }}>No Bots Found</h3>
                <p style={{ color: '#94a3b8' }}>There are no bots configured yet.</p>
              </div>
            ) : (
              <div
                style={{
                  display: 'grid',
                  gridTemplateColumns: 'repeat(auto-fill, minmax(360px, 1fr))',
                  gap: '20px',
                  paddingBottom: '16px'
                }}
              >
                {bots.map((bot, index) => (
                  <div
                    key={bot.id || index}
                    onClick={() => handleBotClick(bot)}
                    className="modern-bot-card"
                  >
                    {/* Status Badge */}
                    <div style={{
                      position: 'absolute',
                      top: '16px',
                      right: '16px',
                      padding: '4px 10px',
                      borderRadius: '12px',
                      backgroundColor: bot.disabled ? '#fee2e2' : (!bot.active ? '#ffedd5' : '#d1fae5'),
                      color: bot.disabled ? '#991b1b' : (!bot.active ? '#c2410c' : '#065f46'),
                      fontSize: '10px',
                      fontWeight: 700,
                      textTransform: 'uppercase',
                      letterSpacing: '0.05em'
                    }}>
                      {getStatusText(bot)}
                    </div>

                    {/* Bot Avatar and Name */}
                    <div style={{ display: 'flex', alignItems: 'center', marginBottom: '16px' }}>
                      <div style={{
                        width: '64px',
                        height: '64px',
                        borderRadius: '12px',
                        overflow: 'hidden',
                        marginRight: '16px',
                        backgroundColor: '#f8fafc',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        border: '1px solid #cbd5e1',
                        boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.05)',
                        flexShrink: 0
                      }}>
                        <img
                          src={getBotAvatar(bot)}
                          alt={bot.name}
                          style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                          onError={(e) => {
                            const target = e.target as HTMLImageElement
                            target.src = `https://ui-avatars.com/api/?name=${encodeURIComponent(bot.name)}&background=5C7CFA&color=fff&size=128`
                          }}
                        />
                      </div>
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <h3 style={{
                          margin: 0,
                          fontSize: '16px',
                          fontWeight: 600,
                          color: '#0f172a',
                          whiteSpace: 'nowrap',
                          overflow: 'hidden',
                          textOverflow: 'ellipsis'
                        }}>
                          {bot.name}
                        </h3>
                        <p style={{
                          margin: '4px 0 0 0',
                          fontSize: '12px',
                          color: '#64748b',
                          fontFamily: 'monospace',
                          whiteSpace: 'nowrap',
                          overflow: 'hidden',
                          textOverflow: 'ellipsis'
                        }}>
                          ID: {bot.id}
                        </p>
                      </div>
                    </div>

                    {/* Description */}
                    <div style={{ marginBottom: '16px', minHeight: '60px' }}>
                      <p style={{
                        margin: 0,
                        fontSize: '13px',
                        color: '#475569',
                        lineHeight: '1.6',
                        display: '-webkit-box',
                        WebkitLineClamp: 3,
                        WebkitBoxOrient: 'vertical',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis'
                      }}>
                        {bot.description || 'No description available for this bot.'}
                      </p>
                    </div>

                    {/* Channels Section */}
                    <div>
                      <div style={{
                        fontSize: '11px',
                        fontWeight: 700,
                        color: '#64748b',
                        marginBottom: '8px',
                        textTransform: 'uppercase',
                        letterSpacing: '0.05em'
                      }}>
                        Active Channels
                      </div>
                      <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px' }}>
                        {getActiveChannels(bot).length > 0 ? (
                          getActiveChannels(bot).map((channel, idx) => (
                            <div
                              key={idx}
                              style={{
                                display: 'flex',
                                alignItems: 'center',
                                padding: '4px 10px',
                                borderRadius: '8px',
                                backgroundColor: '#f1f5f9',
                                border: '1px solid #e2e8f0',
                                color: '#475569',
                                fontSize: '11px',
                                fontWeight: 600,
                                gap: '6px'
                              }}
                            >
                              <Icon icon={getChannelIcon(channel) as any} iconSize={12} color="#64748b" />
                              <span style={{ textTransform: 'capitalize' }}>{channel}</span>
                            </div>
                          ))
                        ) : (
                          <span style={{ fontSize: '12px', color: '#94a3b8', fontStyle: 'italic' }}>
                            No channels configured
                          </span>
                        )}
                      </div>
                    </div>

                    {/* Divider and Card Actions */}
                    <div style={{
                      marginTop: '20px',
                      paddingTop: '12px',
                      borderTop: '1px solid #e2e8f0',
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center'
                    }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '4px', color: '#04a9e1', fontSize: '12px', fontWeight: 600 }}>
                        <span>Configure Bot</span>
                        <Icon icon="arrow-right" iconSize={12} color="#04a9e1" />
                      </div>
                      <Button
                        onClick={(e) => toggleBotStatus(bot, e)}
                        title={bot.disabled ? 'Enable Bot' : 'Disable Bot'}
                        className={`modern-btn-secondary ${bot.disabled ? 'modern-btn-enable' : 'modern-btn-disable'}`}
                        style={{
                          fontSize: '11px',
                          padding: '4px 10px'
                        }}
                        icon={bot.disabled ? 'play' : 'stop'}
                      >
                        {bot.disabled ? 'Enable' : 'Disable'}
                      </Button>
                    </div>

                  </div>
                ))}
              </div>
            )}
          </div>
        </div>

        <div className={Classes.DIALOG_FOOTER}>
          <div className={Classes.DIALOG_FOOTER_ACTIONS} style={{ paddingRight: '8px' }}>
            <Button onClick={toggle} className="modern-btn-primary">
              Close
            </Button>
          </div>
        </div>
      </Dialog>

      {/* Bot Details Dialog */}
      <BotDetailsDialog
        bot={selectedBot}
        isOpen={!!selectedBot}
        onClose={handleCloseDetails}
        getActiveChannels={getActiveChannels}
        getChannelIcon={getChannelIcon}
        getChannelColor={getChannelColor}
        getBotAvatar={getBotAvatar}
        getStatusColor={getStatusColor}
        getStatusText={getStatusText}
      />

      {/* Confirmation Dialog */}
      <Dialog
        icon="warning-sign"
        isOpen={confirmDialogOpen}
        onClose={handleCancelToggle}
        title="Confirm Status Change"
        className="modern-dialog"
        style={{ width: '450px' }}
      >
        <div className={Classes.DIALOG_BODY}>
          {botToToggle && (
            <div style={{ padding: '10px 0' }}>
              <p style={{ fontSize: '15px', lineHeight: '1.6', marginBottom: '15px', color: '#334155' }}>
                Are you sure you want to <strong>{botToToggle.disabled ? 'enable' : 'disable'}</strong> the bot{' '}
                <strong style={{ color: '#04a9e1' }}>"{botToToggle.name}"</strong>?
              </p>
              {!botToToggle.disabled && (
                <div style={{
                  padding: '12px',
                  backgroundColor: '#fef3c7',
                  borderLeft: '4px solid #f59e0b',
                  borderRadius: '6px',
                  marginTop: '10px'
                }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <Icon icon="info-sign" iconSize={16} color="#b45309" />
                    <span style={{ fontSize: '13px', color: '#b45309', fontWeight: 500 }}>
                      Disabling this bot will unmount it and make it unavailable to users.
                    </span>
                  </div>
                </div>
              )}
              {botToToggle.disabled && (
                <div style={{
                  padding: '12px',
                  backgroundColor: '#e0f2fe',
                  borderLeft: '4px solid #0284c7',
                  borderRadius: '6px',
                  marginTop: '10px'
                }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <Icon icon="info-sign" iconSize={16} color="#0369a1" />
                    <span style={{ fontSize: '13px', color: '#0369a1', fontWeight: 500 }}>
                      Enabling this bot will mount it and make it available to users.
                    </span>
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
        <div className={Classes.DIALOG_FOOTER}>
          <div className={Classes.DIALOG_FOOTER_ACTIONS}>
            <Button onClick={handleCancelToggle} className="modern-btn-secondary" style={{ padding: '10px 20px', fontSize: '14px' }}>
              Cancel
            </Button>
            <Button
              onClick={handleConfirmToggle}
              className={`modern-btn-primary ${botToToggle?.disabled ? 'modern-btn-primary-success' : 'modern-btn-primary-danger'}`}
              style={{
                padding: '10px 20px',
                fontSize: '14px'
              }}
              icon={botToToggle?.disabled ? 'play' : 'stop'}
            >
              {botToToggle?.disabled ? 'Enable Bot' : 'Disable Bot'}
            </Button>
          </div>
        </div>
      </Dialog>
    </>
  )
}

export default BotsDashboard

// Optional hover styling (if you wire this into a CSS/SCSS file):
// .bots-dashboard-card:hover {
//   box-shadow: 0 6px 16px rgba(15, 23, 42, 0.12);
//   transform: translateY(-1px);
// }
